import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { Injectable, Post } from '@nestjs/common';
import { InjectRepository } from '@nestjs/typeorm';
import { getConnection, MoreThan, Repository } from 'typeorm';
import { CommentsService } from '../comments/comments.service';
import { HierarchyEntity } from '../hierarchy/hierarchy.entity';
import { PollVotingEntity } from '../poll/poll-voting.entity';
import { PollService } from '../poll/poll.service';
import { PostActivityService } from '../post-activity/post-activity.service';
import { PostFilesService } from '../post-files/post-files.service';
import { ProfileService } from '../profile/profile.service';
import { AllPostsResponseModel, PostResponseModel, SingleResponseModel } from './dto/post-response-model';
import { Pagination1Dto, PaginationDto, PinPostDto, PostDTO } from './dto/post.dto';
import { PostEntity } from './post.entity';

@Injectable()
export class PostService {
    constructor (
        @InjectRepository(PostEntity)
        private postRepository: Repository<PostEntity>, 
        @InjectRepository(HierarchyEntity)
        private hierarchyRepo: Repository<HierarchyEntity>,
        private postFilesService: PostFilesService,
        private pollService: PollService, 
        private profileService: ProfileService, 
        private postActService: PostActivityService,
        private cmntService: CommentsService,     
    ) {}

    async createPost(queryParams,postDto:PostDTO,request:Request): Promise<PostResponseModel> {
        const response = new PostResponseModel();
        try {
            if(queryParams.apiVersion == 1) {
                const savedPost = await this.postRepository.save(postDto);
                if(savedPost) {
                    response.status = true;
                    response.data   = savedPost;
                    response.statusCode = 50000;
                    response.internalMessage = 'Post Created Successfully';
                    response.intlCode = 11100;
                } else { 
                    throw new ErrorResponse(11106, 'Post Not Saved')
                }                
            } else {
                const savedPost = await this.postRepository.save(postDto);
                if(savedPost) {
                    response.status = true;
                    response.data   = savedPost;
                    response.statusCode = 50000;
                    response.internalMessage = 'Post Created Successfully';
                    response.intlCode = 11100;
                } else { 
                    throw new ErrorResponse(11106, 'Post Not Saved')
                }
            }
            return response;
        } catch (error) {
            throw error;
        }
    } 

    async createPostWithReturnResult(queryParams, postDto:PostDTO) {
        try {
            if(queryParams.apiVersion == 1) {
                const savedPost = await this.postRepository.save(postDto);
                if(savedPost) {
                    return savedPost;
                } else {
                    return null;
                }
            } else {
                const savedPost = await this.postRepository.save(postDto);
                if(savedPost) {
                    return savedPost;
                } else {
                    return null;
                }
            }
        } catch (error) {
            throw error;
        }
    }

    async createPostAttachments(queryParam, filesData, postId): Promise<PostResponseModel> {
        const response = new PostResponseModel();
        try {
            if(queryParam.apiVersion == 1) {
                const fileUpload = await this.postFilesService.createPostAttachments(filesData);
                if(fileUpload) {
                    const savedPostData = await this.postRepository.findOne({
                        relations:['posttype','postfiles'],
                        where:{Id:postId}
                    });
                    if(savedPostData) {
                        response.status = true;
                        response.data   = savedPostData;
                        response.statusCode = 50000;
                        response.internalMessage = 'Post Created Successfully';
                        response.intlCode = 11100;
                    } else { 
                        throw new ErrorResponse(11106, 'Post Not Saved')
                    }               
                } else {
                    throw new ErrorResponse(11106,'Post saved but issue while uploading image/video');
                }
            } else {
                const fileUpload = await this.postFilesService.createPostAttachments(filesData);
                if(fileUpload) {
                    const savedPostData = await this.postRepository.findOne({
                        relations:['posttype','postfiles'],
                        where:{Id:postId}
                    });
                    if(savedPostData) {
                        response.status = true;
                        response.data   = savedPostData;
                        response.statusCode = 50000;
                        response.internalMessage = 'Post Created Successfully';
                        response.intlCode = 11100;
                    } else { 
                        throw new ErrorResponse(11106, 'Post Not Saved')
                    }               
                } else {
                    throw new ErrorResponse(11106,'Post saved but issue while uploading image/video');
                }
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async createPostPoll(queryParam, dataToPost,postId):Promise<PostResponseModel> {
        const response = new PostResponseModel();
        try {
            if(queryParam.apiVersion == 1) {
                const pollAdd = await this.pollService.createPostPoll(dataToPost);
                if(pollAdd) {
                    const savedPostData = await this.postRepository.findOne({
                        relations:['posttype','poll'],
                        where:{Id:postId}
                    });
                    if(savedPostData) {
                        response.status = true;
                        response.data   = savedPostData;
                        response.statusCode = 50000;
                        response.internalMessage = 'Post Created Successfully';
                        response.intlCode = 11100;
                    } else { 
                        throw new ErrorResponse(11106, 'Post Not Saved')
                    }               
                } else {
                    throw new ErrorResponse(11106,'Post saved but issue while creating poll');
                }
            } else {
                const pollAdd = await this.pollService.createPostPoll(dataToPost);
                if(pollAdd) {
                    const savedPostData = await this.postRepository.findOne({
                        relations:['posttype','poll'],
                        where:{Id:postId}
                    });
                    if(savedPostData) {
                        response.status = true;
                        response.data   = savedPostData;
                        response.statusCode = 50000;
                        response.internalMessage = 'Post Created Successfully';
                        response.intlCode = 11100;
                    } else { 
                        throw new ErrorResponse(11106, 'Post Not Saved')
                    }               
                } else {
                    throw new ErrorResponse(11106,'Post saved but issue while creating poll');
                }
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async pinPost(queryParams,pinPostDto:PinPostDto,request:Request): Promise<SingleResponseModel> {
        const response = new SingleResponseModel();
        try {
            var dt = new Date();                
            if(queryParams.apiVersion == 1) {
                const removeOld = await this.postRepository.update({pinnedBy:pinPostDto.userId},{isPinned:false})
                const update = await this.postRepository.update(pinPostDto.postId, {isPinned:pinPostDto.isPinned,pinDate:dt,pinnedBy:pinPostDto.userId})
                if(update) {
                    response.status = true;
                    response.statusCode = 50000;
                    response.internalMessage = 'Post pinned successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Post pin failed')
                }
            } else {
                const removeOld = await this.postRepository.update({pinnedBy:pinPostDto.userId},{isPinned:false})
                const update = await this.postRepository.update(pinPostDto.postId, {isPinned:pinPostDto.isPinned,pinDate:dt,pinnedBy:pinPostDto.userId})
                if(update) {
                    response.status = true;
                    response.statusCode = 50000;
                    response.internalMessage = 'Post pinned successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Post pin failed')
                }
            }
            return response;
        } catch(error) {
            throw error;
        }
    }

    async updatePost(queryParams, postDto:PostDTO,request:Request): Promise<PostResponseModel> {
        const response = new PostResponseModel();
        try {
            if(queryParams.apiVersion == 1) {
                const updatePost = await this.postRepository.update(postDto.Id,postDto);
                if(updatePost) {
                    const updatedData = await this.postRepository.findOne({
                        where:{Id:postDto.Id}
                    });
                    response.status = true;
                    response.data = updatedData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Post Updated Successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Post Update Failed')
                }
            } else {
                const updatePost = await this.postRepository.update(postDto.Id,postDto);
                if(updatePost) {
                    const updatedData = await this.postRepository.findOne({
                        where:{Id:postDto.Id}
                    });
                    response.status = true;
                    response.data = updatedData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Post Updated Successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Post Update Failed')
                }
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getActivePosts(pageDto:PaginationDto, userId,request:Request):Promise<AllPostsResponseModel> {
        const response = new AllPostsResponseModel();
        try {
            const finData: any[] = [];
            let finPollData: any[] = []; 
            let images: any[] = [];
            let videos: any[] = [];
            /* var users = await this.hierarchyRepo.find({
                where:{}
            }) */
            if(pageDto.apiVersion == 1) {
                if(!pageDto.skip || pageDto.skip == null) {
                    pageDto.skip = 0;
                } 
                if(pageDto.type == 0) {
                    var posts = await this.postRepository.find({
                        where:{status:true,isPinned:true},
                        order: {pinDate: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });
                }
                if(pageDto.type == 1) {
                    var posts = await this.postRepository.find({
                        where:{status:true,isDevelopmentPost:false,isPinned:false},
                        order: {createdAt: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });                    
                } 
                if(pageDto.type == 2) {
                    var posts = await this.postRepository.find({
                        where:{status:true,isDevelopmentPost:false},
                        order: {createdAt: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });
                    /* var posts = await this.postRepository
                    .createQueryBuilder("post")
                    .select("post, lk+dk+fv as totalCount")
                    .addSelect(subQuery => {
                        return subQuery 
                        .select("Sum(IF(postactivity.like_status = true, 1, 0)) as lk, Sum(IF(postactivity.dislike = true, 1, 0)) as dk, Sum(IF(postactivity.fev = true, 1, 0)) as fv")
                        .from(PostEntity, "post")
                        .leftJoinAndSelect('post.postactivity','postactivity','postactivity.postId = post.Id')
                        .where('post.status=:da', { da: true })
                        .groupBy('post.Id');
                    })
                    .orderBy('totalCount','DESC')
                    .skip(pageDto.skip)
                    .take(10)
                    .getMany(); */   
                    console.log('p',posts);    
                }
                if(pageDto.type == 3) {
                    var posts = await this.postRepository
                    .createQueryBuilder("post")
                    .innerJoinAndSelect('post.postactivity','postactivity','postactivity.postId = post.Id')
                    .where('post.status=:da', { da: true })
                    .andWhere('postactivity.fev=:f',{f:true})
                    .andWhere('postactivity.userId=:u',{u:userId})
                    .orderBy('post.createdAt','DESC')
                    .skip(pageDto.skip)
                    .take(10)
                    .getMany();       
                }
                if(pageDto.type == 4) {
                    var posts = await this.postRepository.find({
                        where:{status:true,isDevelopmentPost:true},
                        order: {createdAt: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });
                } 
                if(pageDto.type == 5) {
                    var posts = await this.postRepository.find({
                        where:{status:true, userId:userId},
                        order: {createdAt: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });
                } 
                if(!posts) {
                    throw new ErrorResponse(99908,'Data not found')
                }
                const promises = posts.map(async (post) => {
                    const user = await this.profileService.getUserByUId(post.userId);
                    const ldfCount = await this.postActService.getPostLikDisLikFavCount(post.Id);
                    const cmtCount =  await this.cmntService.getPostCmtsCount(post.Id);
                    const ldfStatus = await this.postActService.getPostLikDisLikFavStatus(post.Id,userId);
                    const postImages = await this.postFilesService.getPostFiles(post.Id, 'image');
                    const postVideos = await this.postFilesService.getPostFiles(post.Id,'video');
                    const postPoll  = await this.pollService.getPostPoll(post.Id);
                    if(postPoll.length > 0) {
                        const pollTotal = await getConnection()
                        .createQueryBuilder()
                        .select("count(*) as cnt")
                        .from(PollVotingEntity, "PollVoting")
                        .where('post_id=:da', { da: post.Id })
                        .orderBy('status','ASC')
                        .getRawOne();
                        const tlCount = pollTotal.cnt;
                        const promises = postPoll.map(async (v) => {
                            const optionTotal = await getConnection()
                            .createQueryBuilder()
                            .select("count(*) as cnt")
                            .from(PollVotingEntity, "PollVoting")
                            .where('post_id=:da', { da: post.Id })
                            .andWhere('poll_id=:pi',{pi:v.Id})
                            .orderBy('status','ASC')
                            .getRawOne();
                            const statusTotal = await getConnection()
                            .createQueryBuilder()
                            .select("count(*) as cnt")
                            .from(PollVotingEntity, "PollVoting")
                            .where('post_id=:da', { da: post.Id })
                            .andWhere('poll_id=:pi',{pi:v.Id})
                            .andWhere('user_id=:ui',{ui:userId})
                            .orderBy('status','ASC')
                            .getRawOne();                        
                            return  {
                                pollId:v.Id,
                                pollValue:v.pollValue,
                                pollType:v.pollType,
                                polCnt: optionTotal.cnt,
                                usta: statusTotal.cnt > 0 ? true : false
                            }
                        })
                        const pda = await Promise.all(promises);
                        finPollData =[];
                        pda.map((f) => { 
                            finPollData.push({
                                pollId: f.pollId,
                                pollType:f.pollType,
                                pollValue:f.pollValue,
                                totalCount:tlCount,
                                pollCount:f.polCnt,
                                percentage: tlCount == 0 ? 0 : Math.round(f.polCnt / (tlCount / 100)),
                                userStatus: f.usta
                            })
                        });
                    } else {
                        finPollData =[];
                    }
                    return {
                        postId:post.Id,
                        isDevelopmentPost:post.isDevelopmentPost,
                        text:post.text,
                        postTime:post.createdAt,
                        postTypeId:post.postTypeId,
                        userId:post.userId,
                        userName: user.firstName,
                        profilePic:user.profilePic,
                        likesCount:ldfCount.likeCount,
                        likeStatus:ldfStatus != null ? ldfStatus.like : false,
                        disLikesCount:ldfCount.dislikeCount,
                        disLikeStatus:ldfStatus != null ? ldfStatus.dislike : false,
                        favCount:ldfCount.fevCount,
                        favStatus:ldfStatus != null ? ldfStatus.fev : false,
                        commentCount:cmtCount.cnt,
                        postImages:postImages,
                        postVideos:postVideos,
                        poll:finPollData,
                        pollExpire:post.pollExpire,
                        viewCount:post.viewCount
                    }   
                });   
                const da = await Promise.all(promises);
                da.map((f) => {  
                    if(f.postImages) {
                        f.postImages.map((i) => {
                            images.push(`${process.env.WORKINGURL}/${i.fileUrl}`)
                        })
                    } 
                    if(f.postVideos) { 
                        f.postVideos.map((v) => {
                            videos.push(`${process.env.WORKINGURL}/${v.fileUrl}`)
                        })
                    }
                    var day_start = new Date(f.postTime);
                    var day_end = new Date();
                    var total_days = (day_end.valueOf() - day_start.valueOf()) / (1000 * 60 * 60 * 24);
                    var finalDys = Math.round(total_days);
                    
                    if(f.pollExpire != null){
                        var day_start1 = new Date();
                        var day_end1 = new Date(f.pollExpire);
                        var total_days1 = (day_end1.valueOf() - day_start1.valueOf()) / (1000 * 60 * 60 * 24);
                        var finalDys1 = Math.round(total_days1);
                    } else {
                        var finalDys1 = 0;
                    }
                    
                    finData.push({
                        postId:f.postId,
                        isDevelopmentPost:f.isDevelopmentPost,
                        text:f.text,
                        postDays:finalDys+' day(s) ago',
                        postTime:f.postTime,
                        postTypeId:f.postTypeId,
                        userId:f.userId,
                        userName:f.userName,
                        profilePic: f.profilePic != null ? `${process.env.WORKINGURL}/${f.profilePic}` : '',
                        likesCount:f.likesCount != null ? `${f.likesCount}` : 0,
                        likeStatus:f.likeStatus,
                        disLikesCount:f.disLikesCount !=null ? `${f.disLikesCount}` : 0,
                        disLikeStatus:f.disLikeStatus,
                        favCount:f.favCount !=null ? `${f.favCount}` : 0,
                        favStatus:f.favStatus,
                        commentCount:f.commentCount !=null ? `${f.commentCount}` : 0,
                        postImages:images,
                        postVideos:videos,
                        poll:f.poll,
                        pollExpire:finalDys1+' day(s)',
                        viewCount:f.viewCount
                    }) 
                    images=[];
                    videos=[];
                }) 
                if(finData.length > 0) { 
                    response.status = true;
                    response.data = finData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Data retrieved successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Data not found');
                } 
            } else {
                if(!pageDto.skip || pageDto.skip == null) {
                    pageDto.skip = 0;
                } 
                if(pageDto.type == 0) {
                    var posts = await this.postRepository.find({
                        where:{status:true,isPinned:true},
                        order: {pinDate: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });
                }
                if(pageDto.type == 1) {
                    var posts = await this.postRepository.find({
                        where:{status:true,isDevelopmentPost:false,isPinned:false},
                        order: {createdAt: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });                    
                } 
                if(pageDto.type == 2) {
                    var posts = await this.postRepository.find({
                        where:{status:true,isDevelopmentPost:false},
                        order: {createdAt: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });
                    /* var posts = await this.postRepository
                    .createQueryBuilder("post")
                    .select("post, lk+dk+fv as totalCount")
                    .addSelect(subQuery => {
                        return subQuery 
                        .select("Sum(IF(postactivity.like_status = true, 1, 0)) as lk, Sum(IF(postactivity.dislike = true, 1, 0)) as dk, Sum(IF(postactivity.fev = true, 1, 0)) as fv")
                        .from(PostEntity, "post")
                        .leftJoinAndSelect('post.postactivity','postactivity','postactivity.postId = post.Id')
                        .where('post.status=:da', { da: true })
                        .groupBy('post.Id');
                    })
                    .orderBy('totalCount','DESC')
                    .skip(pageDto.skip)
                    .take(10)
                    .getMany(); */   
                    console.log('p',posts);    
                }
                if(pageDto.type == 3) {
                    var posts = await this.postRepository
                    .createQueryBuilder("post")
                    .innerJoinAndSelect('post.postactivity','postactivity','postactivity.postId = post.Id')
                    .where('post.status=:da', { da: true })
                    .andWhere('postactivity.fev=:f',{f:true})
                    .andWhere('postactivity.userId=:u',{u:userId})
                    .orderBy('post.createdAt','DESC')
                    .skip(pageDto.skip)
                    .take(10)
                    .getMany();       
                }
                if(pageDto.type == 4) {
                    var posts = await this.postRepository.find({
                        where:{status:true,isDevelopmentPost:true},
                        order: {createdAt: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });
                } 
                if(pageDto.type == 5) {
                    var posts = await this.postRepository.find({
                        where:{status:true, userId:userId},
                        order: {createdAt: "DESC"},
                        skip:pageDto.skip,
                        take: 10
                    });
                } 
                if(!posts) {
                    throw new ErrorResponse(99908,'Data not found')
                }
                const promises = posts.map(async (post) => {
                    const user = await this.profileService.getUserByUId(post.userId);
                    const ldfCount = await this.postActService.getPostLikDisLikFavCount(post.Id);
                    const cmtCount =  await this.cmntService.getPostCmtsCount(post.Id);
                    const ldfStatus = await this.postActService.getPostLikDisLikFavStatus(post.Id,userId);
                    const postImages = await this.postFilesService.getPostFiles(post.Id, 'image');
                    const postVideos = await this.postFilesService.getPostFiles(post.Id,'video');
                    const postPoll  = await this.pollService.getPostPoll(post.Id);
                    if(postPoll.length > 0) {
                        const pollTotal = await getConnection()
                        .createQueryBuilder()
                        .select("count(*) as cnt")
                        .from(PollVotingEntity, "PollVoting")
                        .where('post_id=:da', { da: post.Id })
                        .orderBy('status','ASC')
                        .getRawOne();
                        const tlCount = pollTotal.cnt;
                        const promises = postPoll.map(async (v) => {
                            const optionTotal = await getConnection()
                            .createQueryBuilder()
                            .select("count(*) as cnt")
                            .from(PollVotingEntity, "PollVoting")
                            .where('post_id=:da', { da: post.Id })
                            .andWhere('poll_id=:pi',{pi:v.Id})
                            .orderBy('status','ASC')
                            .getRawOne();
                            const statusTotal = await getConnection()
                            .createQueryBuilder()
                            .select("count(*) as cnt")
                            .from(PollVotingEntity, "PollVoting")
                            .where('post_id=:da', { da: post.Id })
                            .andWhere('poll_id=:pi',{pi:v.Id})
                            .andWhere('user_id=:ui',{ui:userId})
                            .orderBy('status','ASC')
                            .getRawOne();                        
                            return  {
                                pollId:v.Id,
                                pollValue:v.pollValue,
                                pollType:v.pollType,
                                polCnt: optionTotal.cnt,
                                usta: statusTotal.cnt > 0 ? true : false
                            }
                        })
                        const pda = await Promise.all(promises);
                        finPollData =[];
                        pda.map((f) => { 
                            finPollData.push({
                                pollId: f.pollId,
                                pollType:f.pollType,
                                pollValue:f.pollValue,
                                totalCount:tlCount,
                                pollCount:f.polCnt,
                                percentage: tlCount == 0 ? 0 : Math.round(f.polCnt / (tlCount / 100)),
                                userStatus: f.usta
                            })
                        });
                    } else {
                        finPollData =[];
                    }
                    return {
                        postId:post.Id,
                        isDevelopmentPost:post.isDevelopmentPost,
                        text:post.text,
                        postTime:post.createdAt,
                        postTypeId:post.postTypeId,
                        userId:post.userId,
                        userName: user.firstName,
                        profilePic:user.profilePic,
                        likesCount:ldfCount.likeCount,
                        likeStatus:ldfStatus != null ? ldfStatus.like : false,
                        disLikesCount:ldfCount.dislikeCount,
                        disLikeStatus:ldfStatus != null ? ldfStatus.dislike : false,
                        favCount:ldfCount.fevCount,
                        favStatus:ldfStatus != null ? ldfStatus.fev : false,
                        commentCount:cmtCount.cnt,
                        postImages:postImages,
                        postVideos:postVideos,
                        poll:finPollData,
                        pollExpire:post.pollExpire,
                        viewCount:post.viewCount
                    }   
                });   
                const da = await Promise.all(promises);
                da.map((f) => {  
                    if(f.postImages) {
                        f.postImages.map((i) => {
                            images.push(`${process.env.WORKINGURL}/${i.fileUrl}`)
                        })
                    } 
                    if(f.postVideos) { 
                        f.postVideos.map((v) => {
                            videos.push(`${process.env.WORKINGURL}/${v.fileUrl}`)
                        })
                    }
                    var day_start = new Date(f.postTime);
                    var day_end = new Date();
                    var total_days = (day_end.valueOf() - day_start.valueOf()) / (1000 * 60 * 60 * 24);
                    var finalDys = Math.round(total_days);
                    if(f.pollExpire != null){
                        var day_start1 = new Date();
                        var day_end1 = new Date(f.pollExpire);
                        var total_days1 = (day_end1.valueOf() - day_start1.valueOf()) / (1000 * 60 * 60 * 24);
                        var finalDys1 = Math.round(total_days1);
                    } else {
                        var finalDys1 = 0;
                    }
                    
                    finData.push({
                        postId:f.postId,
                        isDevelopmentPost:f.isDevelopmentPost,
                        text:f.text,
                        postDays:finalDys+' day(s) ago',
                        postTime:f.postTime,
                        postTypeId:f.postTypeId,
                        userId:f.userId,
                        userName:f.userName,
                        profilePic: f.profilePic != null ? `${process.env.WORKINGURL}/${f.profilePic}` : '',
                        likesCount:f.likesCount != null ? `${f.likesCount}` : 0,
                        likeStatus:f.likeStatus,
                        disLikesCount:f.disLikesCount !=null ? `${f.disLikesCount}` : 0,
                        disLikeStatus:f.disLikeStatus,
                        favCount:f.favCount !=null ? `${f.favCount}` : 0,
                        favStatus:f.favStatus,
                        commentCount:f.commentCount !=null ? `${f.commentCount}` : 0,
                        postImages:images,
                        postVideos:videos,
                        poll:f.poll,
                        pollExpire:finalDys1+' day(s)',
                        viewCount:f.viewCount
                    }) 
                    images=[];
                    videos=[];
                }) 
                if(finData.length > 0) { 
                    response.status = true;
                    response.data = finData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Data retrieved successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Data not found');
                } 
            }
            return response;
        } catch(error) {
           throw error;//new ErrorResponse(99998, error);
        }
    }

    async getPostById(queryParams, postId,userId,request:Request):Promise<SingleResponseModel> {
        const response = new SingleResponseModel();
        try {
            let images: any[] = [];
            let videos: any[] = [];
            let finPollData: any[] =[];
            if (queryParams.apiVersion == 1) {
                var post = await this.postRepository.findOne({
                    where:{Id:postId}
                });                     
                if(!post) {
                    throw new ErrorResponse(99908,'Data not found')
                }
                const user = await this.profileService.getUserByUId(post.userId);
                const ldfCount = await this.postActService.getPostLikDisLikFavCount(post.Id);
                const cmtCount =  await this.cmntService.getPostCmtsCount(post.Id);
                const ldfStatus = await this.postActService.getPostLikDisLikFavStatus(post.Id,userId);
                const postImages = await this.postFilesService.getPostFiles(post.Id, 'image');
                const postVideos = await this.postFilesService.getPostFiles(post.Id,'video');
                const postPoll  = await this.pollService.getPostPoll(post.Id);
                if(postPoll.length > 0) {
                    const pollTotal = await getConnection()
                    .createQueryBuilder()
                    .select("count(*) as cnt")
                    .from(PollVotingEntity, "PollVoting")
                    .where('post_id=:da', { da: post.Id })
                    .orderBy('status','ASC')
                    .getRawOne();
                    const tlCount = pollTotal.cnt;
                    const promises = postPoll.map(async (v) => {
                        const optionTotal = await getConnection()
                        .createQueryBuilder()
                        .select("count(*) as cnt")
                        .from(PollVotingEntity, "PollVoting")
                        .where('post_id=:da', { da: post.Id })
                        .andWhere('poll_id=:pi',{pi:v.Id})
                        .orderBy('status','ASC')
                        .getRawOne();
                        const statusTotal = await getConnection()
                        .createQueryBuilder()
                        .select("count(*) as cnt")
                        .from(PollVotingEntity, "PollVoting")
                        .where('post_id=:da', { da: post.Id })
                        .andWhere('poll_id=:pi',{pi:v.Id})
                        .andWhere('user_id=:ui',{ui:userId})
                        .orderBy('status','ASC')
                        .getRawOne();                        
                        return  {
                            pollId:v.Id,
                            pollValue:v.pollValue,
                            pollType:v.pollType,
                            polCnt: optionTotal.cnt,
                            usta: statusTotal.cnt > 0 ? true : false
                        }
                    })
                    const da = await Promise.all(promises);
                    da.map((f) => { 
                        finPollData.push({
                            pollId: f.pollId,
                            pollType:f.pollType,
                            pollValue:f.pollValue,
                            totalCount:tlCount,
                            pollCount:f.polCnt,
                            percentage: tlCount == 0 ? 0 : Math.round(f.polCnt / (tlCount / 100)),
                            userStatus: f.usta
                        })
                    });
                } else {
                    finPollData = [];
                }
                if(postImages) {
                    postImages.map((i) => {
                        images.push(`${process.env.WORKINGURL}/${i.fileUrl}`)
                    })
                } 
                if(postVideos) { 
                    postVideos.map((v) => {
                        videos.push(`${process.env.WORKINGURL}/${v.fileUrl}`)
                    })
                }
                var day_start = new Date(post.createdAt);
                var day_end = new Date();
                var total_days = (day_end.valueOf() - day_start.valueOf()) / (1000 * 60 * 60 * 24);
                var finalDys = Math.round(total_days);
                if(post.pollExpire != null) {
                    var day_start1 = new Date();
                    var day_end1 = new Date(post.pollExpire);
                    var total_days1 = (day_end1.valueOf() - day_start1.valueOf()) / (1000 * 60 * 60 * 24);
                    var finalDys1 = Math.round(total_days1);
                } else {
                    var finalDys1 = 0;
                }
                let final = {
                    postId:post.Id,
                    isDevelopmentPost:post.isDevelopmentPost,
                    text:post.text,
                    postDays:finalDys+' days ago',
                    postTime:post.createdAt,
                    postTypeId:post.postTypeId,
                    userId:post.userId,
                    userName:user.firstName,
                    profilePic: user.profilePic != null ? `${process.env.WORKINGURL}/${user.profilePic}` : '',
                    likesCount:ldfCount.likesCount != null ? `${ldfCount.likesCount}` : 0,
                    likeStatus:ldfStatus != null ? ldfStatus.like : false,
                    disLikesCount:ldfCount.disLikesCount !=null ? `${ldfCount.disLikesCount}` : 0,
                    disLikeStatus:ldfStatus != null ? ldfStatus.dislike : false,
                    favCount:ldfCount.favCount != null ? `${ldfCount.favCount}` : 0,
                    favStatus:ldfStatus != null ? ldfStatus.fev : false,
                    commentCount:cmtCount.cnt != null ? cmtCount.cnt : 0,
                    postImages:images,
                    postVideos:videos,
                    poll:finPollData,
                    pollExpire:finalDys1+' day(s)',
                    viewCount:post.viewCount
                }
                response.status = true;
                response.data = final;
                response.statusCode = 50000;
                response.internalMessage = 'Data retrieved successfully';
                response.intlCode = 11108;
            } else {
                var post = await this.postRepository.findOne({
                    where:{Id:postId}
                });                     
                if(!post) {
                    throw new ErrorResponse(99908,'Data not found')
                }
                const user = await this.profileService.getUserByUId(post.userId);
                const ldfCount = await this.postActService.getPostLikDisLikFavCount(post.Id);
                const cmtCount =  await this.cmntService.getPostCmtsCount(post.Id);
                const ldfStatus = await this.postActService.getPostLikDisLikFavStatus(post.Id,userId);
                const postImages = await this.postFilesService.getPostFiles(post.Id, 'image');
                const postVideos = await this.postFilesService.getPostFiles(post.Id,'video');
                const postPoll  = await this.pollService.getPostPoll(post.Id);
                if(postPoll.length > 0) {
                    const pollTotal = await getConnection()
                    .createQueryBuilder()
                    .select("count(*) as cnt")
                    .from(PollVotingEntity, "PollVoting")
                    .where('post_id=:da', { da: post.Id })
                    .orderBy('status','ASC')
                    .getRawOne();
                    const tlCount = pollTotal.cnt;
                    const promises = postPoll.map(async (v) => {
                        const optionTotal = await getConnection()
                        .createQueryBuilder()
                        .select("count(*) as cnt")
                        .from(PollVotingEntity, "PollVoting")
                        .where('post_id=:da', { da: post.Id })
                        .andWhere('poll_id=:pi',{pi:v.Id})
                        .orderBy('status','ASC')
                        .getRawOne();
                        const statusTotal = await getConnection()
                        .createQueryBuilder()
                        .select("count(*) as cnt")
                        .from(PollVotingEntity, "PollVoting")
                        .where('post_id=:da', { da: post.Id })
                        .andWhere('poll_id=:pi',{pi:v.Id})
                        .andWhere('user_id=:ui',{ui:userId})
                        .orderBy('status','ASC')
                        .getRawOne();                        
                        return  {
                            pollId:v.Id,
                            pollValue:v.pollValue,
                            pollType:v.pollType,
                            polCnt: optionTotal.cnt,
                            usta: statusTotal.cnt > 0 ? true : false
                        }
                    })
                    const da = await Promise.all(promises);
                    da.map((f) => { 
                        finPollData.push({
                            pollId: f.pollId,
                            pollType:f.pollType,
                            pollValue:f.pollValue,
                            totalCount:tlCount,
                            pollCount:f.polCnt,
                            percentage: tlCount == 0 ? 0 : Math.round(f.polCnt / (tlCount / 100)),
                            userStatus: f.usta
                        })
                    });
                } else {
                    finPollData = [];
                }
                if(postImages) {
                    postImages.map((i) => {
                        images.push(`${process.env.WORKINGURL}/${i.fileUrl}`)
                    })
                } 
                if(postVideos) { 
                    postVideos.map((v) => {
                        videos.push(`${process.env.WORKINGURL}/${v.fileUrl}`)
                    })
                }
                var day_start = new Date(post.createdAt);
                var day_end = new Date();
                var total_days = (day_end.valueOf() - day_start.valueOf()) / (1000 * 60 * 60 * 24);
                var finalDys = Math.round(total_days);
                if(post.pollExpire != null) {
                    var day_start1 = new Date();
                    var day_end1 = new Date(post.pollExpire);
                    var total_days1 = (day_end1.valueOf() - day_start1.valueOf()) / (1000 * 60 * 60 * 24);
                    var finalDys1 = Math.round(total_days1);
                } else {
                    var finalDys1 = 0;
                }
                let final = {
                    postId:post.Id,
                    isDevelopmentPost:post.isDevelopmentPost,
                    text:post.text,
                    postDays:finalDys+' days ago',
                    postTime:post.createdAt,
                    postTypeId:post.postTypeId,
                    userId:post.userId,
                    userName:user.firstName,
                    profilePic: user.profilePic != null ? `${process.env.WORKINGURL}/${user.profilePic}` : '',
                    likesCount:ldfCount.likesCount != null ? `${ldfCount.likesCount}` : 0,
                    likeStatus:ldfStatus != null ? ldfStatus.like : false,
                    disLikesCount:ldfCount.disLikesCount !=null ? `${ldfCount.disLikesCount}` : 0,
                    disLikeStatus:ldfStatus != null ? ldfStatus.dislike : false,
                    favCount:ldfCount.favCount != null ? `${ldfCount.favCount}` : 0,
                    favStatus:ldfStatus != null ? ldfStatus.fev : false,
                    commentCount:cmtCount.cnt != null ? cmtCount.cnt : 0,
                    postImages:images,
                    postVideos:videos,
                    poll:finPollData,
                    pollExpire:finalDys1+' day(s)',
                    viewCount:post.viewCount
                }
                response.status = true;
                response.data = final;
                response.statusCode = 50000;
                response.internalMessage = 'Data retrieved successfully';
                response.intlCode = 11108;
            }
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getLatestRefreshedPosts(pageDto:Pagination1Dto, userId,request:Request):Promise<AllPostsResponseModel> {
        const response = new AllPostsResponseModel();
        try {
            const finData: any[] = [];
            let finPollData: any[] = [];
            let images: any[] = [];
            let videos: any[] = [];
            if(pageDto.apiVersion == 1) {
                var posts = await this.postRepository.find({
                    where:{status:true,isDevelopmentPost:false,Id:MoreThan(pageDto.lastPostId)},
                    order: {createdAt: "DESC"},
                    skip:pageDto.skip,
                    take: 10
                });
                if(!posts) {
                    throw new ErrorResponse(99908,'Data not found')
                }
                const promises = posts.map(async (post) => {
                    const user = await this.profileService.getUserByUId(post.userId);
                    const ldfCount = await this.postActService.getPostLikDisLikFavCount(post.Id);
                    const cmtCount =  await this.cmntService.getPostCmtsCount(post.Id);
                    const ldfStatus = await this.postActService.getPostLikDisLikFavStatus(post.Id,userId);
                    const postImages = await this.postFilesService.getPostFiles(post.Id, 'image');
                    const postVideos = await this.postFilesService.getPostFiles(post.Id,'video');
                    const postPoll  = await this.pollService.getPostPoll(post.Id);
                    if(postPoll.length > 0) {
                        const pollTotal = await getConnection()
                        .createQueryBuilder()
                        .select("count(*) as cnt")
                        .from(PollVotingEntity, "PollVoting")
                        .where('post_id=:da', { da: post.Id })
                        .orderBy('status','ASC')
                        .getRawOne();
                        const tlCount = pollTotal.cnt;
                        const promises = postPoll.map(async (v) => {
                            const optionTotal = await getConnection()
                            .createQueryBuilder()
                            .select("count(*) as cnt")
                            .from(PollVotingEntity, "PollVoting")
                            .where('post_id=:da', { da: post.Id })
                            .andWhere('poll_id=:pi',{pi:v.Id})
                            .orderBy('status','ASC')
                            .getRawOne();
                            const statusTotal = await getConnection()
                            .createQueryBuilder()
                            .select("count(*) as cnt")
                            .from(PollVotingEntity, "PollVoting")
                            .where('post_id=:da', { da: post.Id })
                            .andWhere('poll_id=:pi',{pi:v.Id})
                            .andWhere('user_id=:ui',{ui:userId})
                            .orderBy('status','ASC')
                            .getRawOne();                        
                            return  {
                                pollId:v.Id,
                                pollValue:v.pollValue,
                                pollType:v.pollType,
                                polCnt: optionTotal.cnt,
                                usta: statusTotal.cnt > 0 ? true : false
                            }
                        })
                        const pda = await Promise.all(promises);
                        finPollData =[];
                        pda.map((f) => { 
                            finPollData.push({
                                pollId: f.pollId,
                                pollType:f.pollType,
                                pollValue:f.pollValue,
                                totalCount:tlCount,
                                pollCount:f.polCnt,
                                percentage: tlCount == 0 ? 0 : Math.round(f.polCnt / (tlCount / 100)),
                                userStatus: f.usta
                            })
                        });
                    } else {
                        finPollData =[];
                    }
                    return {
                        postId:post.Id,
                        isDevelopmentPost:post.isDevelopmentPost,
                        text:post.text,
                        postTime:post.createdAt,
                        postTypeId:post.postTypeId,
                        userId:post.userId,
                        userName: user.firstName,
                        profilePic:user.profilePic,
                        likesCount:ldfCount.likeCount,
                        likeStatus:ldfStatus != null ? ldfStatus.like : false,
                        disLikesCount:ldfCount.dislikeCount,
                        disLikeStatus:ldfStatus != null ? ldfStatus.dislike : false,
                        favCount:ldfCount.fevCount,
                        favStatus:ldfStatus != null ? ldfStatus.fev : false,
                        commentCount:cmtCount.cnt,
                        postImages:postImages,
                        postVideos:postVideos,
                        poll:finPollData,
                        pollExpire:post.pollExpire,
                        viewCount:post.viewCount
                    }   
                });   
                const da = await Promise.all(promises);
                da.map((f) => {  
                    if(f.postImages) {
                        f.postImages.map((i) => {
                            images.push(`${process.env.WORKINGURL}/${i.fileUrl}`)
                        })
                    } 
                    if(f.postVideos) { 
                        f.postVideos.map((v) => {
                            videos.push(`${process.env.WORKINGURL}/${v.fileUrl}`)
                        })
                    }
                    var day_start = new Date(f.postTime);
                    var day_end = new Date();
                    var total_days = (day_end.valueOf() - day_start.valueOf()) / (1000 * 60 * 60 * 24);
                    var finalDys = Math.round(total_days);
                    if(f.pollExpire != null) {
                        var day_start1 = new Date();
                        var day_end1 = new Date();
                        var total_days1 = (day_end1.valueOf() - day_start1.valueOf()) / (1000 * 60 * 60 * 24);
                        var finalDys1 = Math.round(total_days1);
                    } else {
                        var finalDys1 = 0;
                    }
                    finData.push({
                        postId:f.postId,
                        isDevelopmentPost:f.isDevelopmentPost,
                        text:f.text,
                        postDays:finalDys+' days ago',
                        postTime:f.postTime,
                        postTypeId:f.postTypeId,
                        userId:f.userId,
                        userName:f.userName,
                        profilePic: f.profilePic != null ? `${process.env.WORKINGURL}/${f.profilePic}` : '',
                        likesCount:f.likesCount != null ? `${f.likesCount}` : 0,
                        likeStatus:f.likeStatus,
                        disLikesCount:f.disLikesCount !=null ? `${f.disLikesCount}` : 0,
                        disLikeStatus:f.disLikeStatus,
                        favCount:f.favCount !=null ? `${f.favCount}` : 0,
                        favStatus:f.favStatus,
                        commentCount:f.commentCount !=null ? `${f.commentCount}` : 0,
                        postImages:images,
                        postVideos:videos,
                        poll:f.poll,
                        pollExpire: finalDys1,
                        viewCount:f.viewCount
                    }) 
                    images=[];
                    videos=[];
                }) 
                if(finData.length > 0) { 
                    response.status = true;
                    response.data = finData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Data retrieved successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Data not found');
                } 
            } else {
                var posts = await this.postRepository.find({
                    where:{status:true,isDevelopmentPost:false,Id:MoreThan(pageDto.lastPostId)},
                    order: {createdAt: "DESC"},
                    skip:pageDto.skip,
                    take: 10
                });
                if(!posts) {
                    throw new ErrorResponse(99908,'Data not found')
                }
                const promises = posts.map(async (post) => {
                    const user = await this.profileService.getUserByUId(post.userId);
                    const ldfCount = await this.postActService.getPostLikDisLikFavCount(post.Id);
                    const cmtCount =  await this.cmntService.getPostCmtsCount(post.Id);
                    const ldfStatus = await this.postActService.getPostLikDisLikFavStatus(post.Id,userId);
                    const postImages = await this.postFilesService.getPostFiles(post.Id, 'image');
                    const postVideos = await this.postFilesService.getPostFiles(post.Id,'video');
                    const postPoll  = await this.pollService.getPostPoll(post.Id);
                    if(postPoll.length > 0) {
                        const pollTotal = await getConnection()
                        .createQueryBuilder()
                        .select("count(*) as cnt")
                        .from(PollVotingEntity, "PollVoting")
                        .where('post_id=:da', { da: post.Id })
                        .orderBy('status','ASC')
                        .getRawOne();
                        const tlCount = pollTotal.cnt;
                        const promises = postPoll.map(async (v) => {
                            const optionTotal = await getConnection()
                            .createQueryBuilder()
                            .select("count(*) as cnt")
                            .from(PollVotingEntity, "PollVoting")
                            .where('post_id=:da', { da: post.Id })
                            .andWhere('poll_id=:pi',{pi:v.Id})
                            .orderBy('status','ASC')
                            .getRawOne();
                            const statusTotal = await getConnection()
                            .createQueryBuilder()
                            .select("count(*) as cnt")
                            .from(PollVotingEntity, "PollVoting")
                            .where('post_id=:da', { da: post.Id })
                            .andWhere('poll_id=:pi',{pi:v.Id})
                            .andWhere('user_id=:ui',{ui:userId})
                            .orderBy('status','ASC')
                            .getRawOne();                        
                            return  {
                                pollId:v.Id,
                                pollValue:v.pollValue,
                                pollType:v.pollType,
                                polCnt: optionTotal.cnt,
                                usta: statusTotal.cnt > 0 ? true : false
                            }
                        })
                        const pda = await Promise.all(promises);
                        finPollData =[];
                        pda.map((f) => { 
                            finPollData.push({
                                pollId: f.pollId,
                                pollType:f.pollType,
                                pollValue:f.pollValue,
                                totalCount:tlCount,
                                pollCount:f.polCnt,
                                percentage: tlCount == 0 ? 0 : Math.round(f.polCnt / (tlCount / 100)),
                                userStatus: f.usta
                            })
                        });
                    } else {
                        finPollData =[];
                    }
                    return {
                        postId:post.Id,
                        isDevelopmentPost:post.isDevelopmentPost,
                        text:post.text,
                        postTime:post.createdAt,
                        postTypeId:post.postTypeId,
                        userId:post.userId,
                        userName: user.firstName,
                        profilePic:user.profilePic,
                        likesCount:ldfCount.likeCount,
                        likeStatus:ldfStatus != null ? ldfStatus.like : false,
                        disLikesCount:ldfCount.dislikeCount,
                        disLikeStatus:ldfStatus != null ? ldfStatus.dislike : false,
                        favCount:ldfCount.fevCount,
                        favStatus:ldfStatus != null ? ldfStatus.fev : false,
                        commentCount:cmtCount.cnt,
                        postImages:postImages,
                        postVideos:postVideos,
                        poll:finPollData,
                        pollExpire:post.pollExpire,
                        viewCount:post.viewCount
                    }   
                });   
                const da = await Promise.all(promises);
                da.map((f) => {  
                    if(f.postImages) {
                        f.postImages.map((i) => {
                            images.push(`${process.env.WORKINGURL}/${i.fileUrl}`)
                        })
                    } 
                    if(f.postVideos) { 
                        f.postVideos.map((v) => {
                            videos.push(`${process.env.WORKINGURL}/${v.fileUrl}`)
                        })
                    }
                    var day_start = new Date(f.postTime);
                    var day_end = new Date();
                    var total_days = (day_end.valueOf() - day_start.valueOf()) / (1000 * 60 * 60 * 24);
                    var finalDys = Math.round(total_days);
                    if(f.pollExpire != null) {
                        var day_start1 = new Date();
                        var day_end1 = new Date();
                        var total_days1 = (day_end1.valueOf() - day_start1.valueOf()) / (1000 * 60 * 60 * 24);
                        var finalDys1 = Math.round(total_days1);
                    } else {
                        var finalDys1 = 0;
                    }
                    finData.push({
                        postId:f.postId,
                        isDevelopmentPost:f.isDevelopmentPost,
                        text:f.text,
                        postDays:finalDys+' days ago',
                        postTime:f.postTime,
                        postTypeId:f.postTypeId,
                        userId:f.userId,
                        userName:f.userName,
                        profilePic: f.profilePic != null ? `${process.env.WORKINGURL}/${f.profilePic}` : '',
                        likesCount:f.likesCount != null ? `${f.likesCount}` : 0,
                        likeStatus:f.likeStatus,
                        disLikesCount:f.disLikesCount !=null ? `${f.disLikesCount}` : 0,
                        disLikeStatus:f.disLikeStatus,
                        favCount:f.favCount !=null ? `${f.favCount}` : 0,
                        favStatus:f.favStatus,
                        commentCount:f.commentCount !=null ? `${f.commentCount}` : 0,
                        postImages:images,
                        postVideos:videos,
                        poll:f.poll,
                        pollExpire: finalDys1,
                        viewCount:f.viewCount
                    }) 
                    images=[];
                    videos=[];
                }) 
                if(finData.length > 0) { 
                    response.status = true;
                    response.data = finData;
                    response.statusCode = 50000;
                    response.internalMessage = 'Data retrieved successfully';
                    response.intlCode = 11108;
                } else {
                    throw new ErrorResponse(99998, 'Data not found');
                } 
            }
            return response;
        } catch(error) {
           throw error;//new ErrorResponse(99998, error);
        }
    }
}
